// Platforms ordered with PRODUCTION first.
export const orderPlatformsProductionFirst = (platforms = []) =>
    [...platforms].sort((a, b) => {
        if (a.type === b.type) return 0;
        return a.type === 'PRODUCTION' ? -1 : 1;
    });

// Platform types, in display order (production first).
export const PLATFORM_TYPES = ['PRODUCTION', 'PREPRODUCTION', 'RECETTE', 'DEMO', 'DEMO_ATS', 'SALON'];

// Client categories accepted by the category badge and filter (M = Mental'o clients).
export const CLIENT_CATEGORIES = ['A', 'B', 'C', 'D', 'M'];

// Translated label of a platform type; unknown values fall back to the raw key.
// Labels are literal t() calls so that `npm run extract-strings` picks them up.
export const getPlatformTypeLabel = (t, type) =>
    ({
        PRODUCTION: t('Production'),
        PREPRODUCTION: t('Preproduction'),
        RECETTE: t('Acceptance testing'),
        DEMO: t('Demo'),
        DEMO_ATS: t('ATS demo'),
        SALON: t('Trade show'),
    })[type] ?? type;

// Select options for platform type pickers.
export const getPlatformTypeOptions = (t) => PLATFORM_TYPES.map((value) => ({ value, label: getPlatformTypeLabel(t, value) }));

// Client types, in display order.
const CLIENT_TYPES = [
    'INTERIM_GENERALIST',
    'INTERIM_TERTIARY',
    'HR',
    'SCHOOL',
    'TRAINING_ORGANIZATION',
    'RECRUITMENT_FIRM',
    'SOFTWARE_VENDOR',
];

// Translated label of a client type (Salesforce business sector); a free-text type is shown as is,
// and a missing one yields null.
export const getClientTypeLabel = (t, clientType) =>
    ({
        INTERIM_GENERALIST: t('Temporary staffing (generalist)'),
        INTERIM_TERTIARY: t('Temporary staffing (office)'),
        HR: t('Corporate HR'),
        SCHOOL: t('School'),
        TRAINING_ORGANIZATION: t('Training organization'),
        RECRUITMENT_FIRM: t('Recruitment firm'),
        SOFTWARE_VENDOR: t('Software vendor'),
    })[clientType] ??
    clientType ??
    null;

// Select options for client type pickers, led by an empty "no type" option.
export const getClientTypeOptions = (t) => [
    { value: '', label: t('No type') },
    ...CLIENT_TYPES.map((value) => ({ value, label: getClientTypeLabel(t, value) })),
];

// Database names a platform can be hosted on (enum App\Backoffice\Entity\Enum\DatabaseName).
const DATABASE_NAMES = [
    'tami_101',
    'tami_111',
    'tami_113',
    'tami_114',
    'tami_121',
    'tami_122',
    'tami_123',
    'tami_124',
    'tami_131',
    'tami_132',
    'tami_133',
    'tami_141',
    'tami_141_recette',
    'tami_142',
    'tami_142_recette',
    'tami_151',
    'tami_151_recette',
    'tami_171',
    'tami_181',
    'tami_41',
    'tami_42',
    'tami_43',
    'tami_51',
    'tami_52',
    'tami_53',
    'tami_54',
    'tami_61',
    'tami_63',
    'tami_64',
    'tami_72',
    'tami_73',
    'tami_81',
    'tami_82',
    'tami_83',
    'tami_84',
    'tami_85',
    'tami_api',
    'tami_demo',
    'tami_etesting',
    'tami_recette',
    'tami_recette_salm',
    'test_42',
];

// Select options for database name pickers.
export const getDatabaseNameOptions = () => DATABASE_NAMES.map((value) => ({ value, label: value }));

// Translated label of a platform's domain owner.
const getDomainOwnerLabel = (t, domainOwner) =>
    ({
        ETESTING: t('E-testing'),
        CLIENT: t('Client'),
    })[domainOwner] ?? domainOwner;

// Select options for domain owner pickers.
export const getDomainOwnerOptions = (t) => ['ETESTING', 'CLIENT'].map((value) => ({ value, label: getDomainOwnerLabel(t, value) }));

// "First Last" of a CSM reference, falling back to the email; null when no CSM is assigned.
export const getCsmDisplayName = (csm) => {
    if (!csm) return null;
    return `${csm.firstName ?? ''} ${csm.lastName ?? ''}`.trim() || csm.email || null;
};

// Select options for CSM pickers, led by an empty "no CSM" option.
export const getCsmOptions = (t, users = []) => [
    { value: '', label: t('No CSM') },
    ...users.map((user) => ({ value: user.uuid, label: getCsmDisplayName(user) })),
];
